/**
 * Investor Portal API Client
 *
 * Dedicated API client for investor portal with separate authentication.
 * Uses a dual-token system: a short-lived access token kept in localStorage
 * and a long-lived refresh token held in an httponly cookie (never exposed to
 * JavaScript, so it cannot be read by injected scripts during an XSS attack).
 */

import { API_URL } from '@/config/api';
import {
  APIError,
  extractDetailMessage,
  resolveRequestId,
} from '@/utils/api-client';
import { errorMessage, log } from '@/utils/logger';

// A 401 from these means bad credentials, not a stale token. Refreshing would
// fail and bounce the user to "session expired" instead of "wrong code".
const NO_REFRESH_ON_401 = [
  '/portal/auth/refresh',
  '/portal/auth/logout',
  '/portal/auth/magic-link',
  '/portal/auth/verify',
  '/portal/auth/dev-login',
];

function toRequestBody(data: unknown): BodyInit | undefined {
  if (data instanceof FormData) return data;
  if (!data) return undefined;
  return JSON.stringify(data);
}

class InvestorPortalAPIClient {
  private baseURL: string;
  private refreshInFlight: Promise<string> | null = null;

  constructor(baseURL: string = API_URL) {
    this.baseURL = baseURL;
  }

  private buildUrl(endpoint: string): string {
    return `${this.baseURL}${endpoint}`;
  }

  /**
   * Get authentication headers using investor portal access token
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('investor_portal_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Store the access token after successful authentication.
   *
   * The refresh token is NOT stored here - it is set by the backend as an
   * httponly cookie and is never readable from JavaScript.
   */
  storeTokens(accessToken: string, expiresIn: number): void {
    localStorage.setItem('investor_portal_token', accessToken);

    // Store expiration time based on backend-provided expires_in (seconds)
    const expiresAt = Date.now() + expiresIn * 1000;
    localStorage.setItem(
      'investor_portal_token_expires_at',
      expiresAt.toString()
    );
  }

  /**
   * Clear all tokens and auth data
   */
  clearTokens(): void {
    localStorage.removeItem('investor_portal_token');
    localStorage.removeItem('investor_portal_refresh_token');
    localStorage.removeItem('investor_portal_token_expires_at');
    localStorage.removeItem('investor_portal_contact');
  }

  /**
   * Check if access token is expired or will expire soon (within 5 minutes)
   */
  private isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('investor_portal_token_expires_at');

    // If no expiration time stored, assume it's from old system (still valid)
    // This handles migration from single-token to dual-token system
    if (!expiresAt) {
      const token = localStorage.getItem('investor_portal_token');
      // If there's a token but no expiration, it's from the old system
      // Don't treat it as expired - let the backend validate it
      return !token;
    }

    const expirationTime = Number.parseInt(expiresAt, 10);
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    return Date.now() >= expirationTime - bufferTime;
  }

  /**
   * Refresh the access token using the refresh token
   *
   * NOTE: Direct fetch() usage is intentional here (exception to CLAUDE.md Rule #2).
   * This is core auth infrastructure that cannot use React Query without creating
   * circular dependencies. This low-level method is called by the API client itself.
   */
  private async refreshAccessToken(): Promise<string> {
    try {
      // The refresh token is sent automatically via the httponly cookie.
      // `credentials: 'include'` ensures the cookie is attached to the request.
      const response = await fetch(this.buildUrl('/portal/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      // Store the new access token. The rotated refresh token is set by the
      // backend as a fresh httponly cookie.
      this.storeTokens(data.access_token, data.expires_in);

      return data.access_token;
    } catch (error) {
      // If refresh fails, clear tokens and redirect to login
      this.clearTokens();
      window.location.href = '/portal/login?sessionExpired=true';
      throw error;
    }
  }

  /**
   * Refresh once per burst. Every caller - the pre-flight expiry check and a
   * 401 alike - joins the same promise, so N concurrent requests rotate the
   * refresh cookie once instead of racing each other onto a consumed one.
   */
  private refreshOnce(): Promise<string> {
    this.refreshInFlight ??= this.refreshAccessToken().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /**
   * Ensure we have a valid access token (refresh if needed)
   */
  private async ensureValidToken(): Promise<void> {
    if (!this.isTokenExpired()) {
      return; // Token is still valid
    }
    await this.refreshOnce();
  }

  /**
   * Handle common HTTP response errors (401, 413, 204)
   */
  private async handleCommonResponseErrors(
    response: Response,
    endpoint: string,
    retryRequest: () => Promise<Response>
  ): Promise<Response> {
    // Handle 401 Unauthorized responses
    if (response.status === 401) {
      if (NO_REFRESH_ON_401.some((path) => endpoint.includes(path))) {
        throw new APIError('Unauthorized', 401);
      }

      try {
        // Joins an in-flight refresh rather than logging the user out; a
        // page-wide refetch produces several 401s at once.
        await this.refreshOnce();
      } catch (refreshError) {
        log.error('Token refresh failed', {
          error_message: errorMessage(refreshError),
        });
        this.clearTokens();
        window.location.href = '/portal/login?sessionExpired=true';
        throw new APIError('Session expired', 401);
      }

      // Retry the original request with new token
      // Let errors from retry propagate naturally with their own status/message
      return await retryRequest();
    }

    // Handle 413 Request Entity Too Large
    if (response.status === 413) {
      throw new APIError('File too large. Maximum upload size is 10MB.', 413);
    }

    return response;
  }

  /**
   * Parse error message from failed response
   */
  private async parseErrorMessage(response: Response): Promise<string> {
    let errorMessage = `Request failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage =
        extractDetailMessage(errorData?.detail) ||
        errorData?.error?.message ||
        errorData?.message ||
        errorMessage;
    } catch {
      // If JSON parsing fails, use the default error message
    }
    return errorMessage;
  }

  /**
   * NOTE: Direct fetch() usage is intentional here (exception to CLAUDE.md Rule #2).
   * This is the low-level HTTP transport layer that React Query hooks ultimately use.
   * Architecture: fetch() → apiClient → services → React Query hooks → components
   */
  async request<T = unknown>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    // Ensure we have a valid token before making the request
    // Skip token refresh for auth endpoints
    if (!endpoint.includes('/portal/auth/')) {
      await this.ensureValidToken();
    }

    const url = this.buildUrl(endpoint);
    const isFormData = options?.body instanceof FormData;
    // Correlation id for cross-app tracing. Derived once so it stays stable
    // across the 401 refresh-and-retry below.
    const requestId = resolveRequestId(options?.headers);

    const makeRequest = async (): Promise<Response> => {
      // Spread caller options first, then apply the merged headers last so a
      // caller-supplied `headers` (e.g. X-Client-Trace-Id) doesn't clobber the
      // whole headers object and drop Content-Type/auth headers.
      const { headers: callerHeaders, ...restOptions } = options ?? {};
      const config: RequestInit = {
        // Send/receive the httponly refresh token cookie on every request so
        // auth endpoints can set it and /refresh can read it.
        credentials: 'include',
        ...restOptions,
        headers: {
          ...(!isFormData && { 'Content-Type': 'application/json' }),
          ...this.getAuthHeaders(),
          'X-Request-ID': requestId,
          ...callerHeaders,
        },
      };

      return await fetch(url, config);
    };

    try {
      let response = await makeRequest();

      // Handle 401 with potential retry
      response = await this.handleCommonResponseErrors(
        response,
        endpoint,
        makeRequest
      );

      if (response.status === 204) {
        return {} as T;
      }

      // Handle JSON responses
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        log.error('Failed to parse JSON response', {
          error_message: errorMessage(parseError),
          status: response.status,
          request_id: requestId,
        });
        if (response.status === 413) {
          throw new APIError(
            'File too large. Maximum upload size is 10MB.',
            413
          );
        } else if (response.status === 404) {
          throw new APIError('Resource not found', 404);
        } else if (response.status === 500) {
          throw new APIError('Server error. Please try again later.', 500);
        }
        throw new APIError(
          'Invalid JSON response from server',
          response.status
        );
      }

      if (!response.ok) {
        const errorMessage =
          extractDetailMessage(data?.detail) ||
          data?.error?.message ||
          data?.message ||
          'API request failed';
        log.error('Investor Portal API error', {
          status: response.status,
          error_message: errorMessage,
          endpoint,
          request_id: requestId,
        });
        throw new APIError(errorMessage, response.status, data);
      }

      if (data === undefined || data === null) {
        log.warn('API returned null/undefined, returning empty object', {
          endpoint,
        });
        return {} as T;
      }

      return data as T;
    } catch (error) {
      if (error instanceof APIError) {
        // Tag with this request's correlation id (covers throws from the
        // helpers, e.g. 401/413) so it reaches the error log downstream.
        error.requestId ??= requestId;
        throw error;
      }
      throw new APIError('Network error', 0, error, requestId);
    }
  }

  async get<T = unknown>(
    endpoint: string,
    options?: { params?: object }
  ): Promise<T> {
    let url = endpoint;

    // Add query parameters if provided
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.request<T>(url, { method: 'GET' });
  }

  async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: toRequestBody(data),
      ...options,
    });
  }

  async put<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: toRequestBody(data),
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: toRequestBody(data),
    });
  }

  async delete<T = unknown>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Fetch a file as a Blob (for viewing/processing in memory)
   *
   * NOTE: Direct fetch() usage is intentional here (exception to CLAUDE.md Rule #2).
   * This is low-level infrastructure for binary file downloads, called by services
   * that are wrapped by React Query hooks.
   */
  async getBlob(endpoint: string): Promise<Blob> {
    // Ensure valid token before fetching
    await this.ensureValidToken();

    const url = this.buildUrl(endpoint);
    const requestId = resolveRequestId();

    // Headers are built per attempt: hoisting them replayed the 401 retry with
    // the same dead token, so the retry could never succeed.
    const makeRequest = async (): Promise<Response> => {
      return await fetch(url, {
        headers: {
          ...this.getAuthHeaders(),
          'X-Request-ID': requestId,
        },
        credentials: 'include',
      });
    };

    try {
      let response = await makeRequest();

      response = await this.handleCommonResponseErrors(
        response,
        endpoint,
        makeRequest
      );

      if (!response.ok) {
        const errorMessage = await this.parseErrorMessage(response);
        throw new APIError(errorMessage, response.status, undefined, requestId);
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof APIError) {
        error.requestId ??= requestId;
        throw error;
      }
      throw new APIError('Network error', 0, error, requestId);
    }
  }
}

export const investorPortalApiClient = new InvestorPortalAPIClient();
